/**
 * Map Viewport Plugin for Search Results Map Variant
 *
 * Translates external signals into map viewport changes — answering the question
 * "given this state change, where should the map be looking?"
 *
 * Current responsibilities:
 * - Re-center the map when the user picks a new search origin from the typeahead
 *
 * Future candidates for this plugin (do NOT add to other plugins):
 * - Re-centering on geolocation (currently in searchWithinMap.plugin)
 * - Zoom resets driven by location type changes
 * - Any other "external event → pan/zoom" mapping
 *
 * This plugin does NOT:
 * - Create or tear down the map instance (see map.plugin)
 * - Trigger searches or manage the search-within-map button (see searchWithinMap.plugin)
 * - React to KML boundary data (see mapBoundaries.plugin)
 *
 * Reason to change: how or when the map viewport should pan/zoom in response
 * to a change in search context.
 *
 * @module searchResultsMap/plugins/mapViewport.plugin
 */

import { selectLocation } from 'features/searchResultsFacets';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { distinctUntilChanged, filter, map, shareReplay, skip, withLatestFrom } from 'rxjs';

export default function createMapViewportPlugin(mapToggle$) {
  const state = {
    started: false,
    mapFacade: null,
    store$: null,
    getBreakpoint: null,
    subscriptions: [],
  };

  function mapToggleByBreakpoint(breakpoint) {
    return breakpoint === LayoutBreakpoint.Big || breakpoint === LayoutBreakpoint.Medium;
  }

  function subscribeToOriginCoordinates() {
    // Shared stream of valid, distinct origin coordinates.
    // shareReplay lets the toggle subscription sample the latest value without re-deriving it.
    const origin$ = state.store$.pipe(
      map(selectLocation),
      map((location) => ({
        originLatitude: Number(location?.originLatitude),
        originLongitude: Number(location?.originLongitude),
      })),
      filter(
        ({ originLatitude, originLongitude }) =>
          !Number.isNaN(originLatitude) && !Number.isNaN(originLongitude)
      ),
      distinctUntilChanged(
        (prev, curr) =>
          prev.originLatitude === curr.originLatitude &&
          prev.originLongitude === curr.originLongitude
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    // Re-center when the origin changes, but only if the map is currently visible.
    const onOriginChange = origin$
      .pipe(
        skip(1), // Skip initial emission used for initial map center
        withLatestFrom(mapToggle$),
        filter(([, isMapToggled]) => mapToggleByBreakpoint(state.getBreakpoint())  || isMapToggled),
        map(([coords]) => coords),
      )
      .subscribe(({ originLatitude, originLongitude }) => {
        state.mapFacade?.updateMapCenter?.({ latitude: originLatitude, longitude: originLongitude });
      });

    // On mobile: when the user opens the map, re-center to the latest origin so
    // they always see the location they searched for.
    const onMapVisible = mapToggle$
      .pipe(
        filter(Boolean),
        filter(() => !mapToggleByBreakpoint(state.getBreakpoint())),
        withLatestFrom(origin$),
        map(([, coords]) => coords),
      )
      .subscribe(({ originLatitude, originLongitude }) => {
        state.mapFacade?.updateMapCenter?.({ latitude: originLatitude, longitude: originLongitude });
      });

    state.subscriptions.push(onOriginChange, onMapVisible);
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'map-viewport',

    async init(context) {
      context.state.mapViewport = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.getBreakpoint = context.services.getBreakpoint;

      if (!state.mapFacade || !state.store$) {
        return;
      }

      subscribeToOriginCoordinates();

      state.started = true;
      context.state.mapViewport = {
        started: true,
      };
    },

    async stop(context) {
      clearSubscriptions();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.getBreakpoint = null;

      delete context.state.mapViewport;
    },
  };
}
